import { Model, FormattedRequestObject, User, ErrorType } from '@/lib/types'
import { canUserAccessModel } from '../models/model-tiers'
import { MAX_FILES } from '@/app/api/uploadthing/core'
import { PricingCore } from '@/lib/pricing/pricing-core'

const GUEST_USER_MAX_TOKENS = 10000


// ANY CHECK THAT CAN BE RUN ON THE CLIENT AND ON THE SERVER SHOULD BE IN THIS FUNCTION THOSE ARE JUST HELPERS

export async function checkUserEligibility(
    selectedModel: Model,
    generationData: FormattedRequestObject,
    userData?: User & { isDangerous?: boolean } | null
): Promise<
{ isAuthorized: true, errorType: null, maxOutputTokens?: number, estimatedCost?: number } | 
{ isAuthorized: false, errorType: ErrorType, maxOutputTokens?: number, estimatedCost?: number }> {
    // return { isAuthorized: false, error: 'test' } 
    let estimatedCost: number | undefined = undefined
    let maxOutputTokens: number | undefined = undefined

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// General checks for any user
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    // Check file limit
    if (generationData.userMessage.files && generationData.userMessage.files.length > MAX_FILES) {
        return { 
            isAuthorized: false, 
            errorType: ErrorType.TOO_MANY_FILES
        }
    }

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// For logged in users
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    if (userData) {
        // Check if user is dangerous
        if (userData?.isDangerous) {
            return { isAuthorized: false, errorType: ErrorType.AUTHORIZATION_FAILED }
        }

        // Check if user has enough balance
        if (userData.balance <= 0) {
            return {
                isAuthorized: false,
                errorType: ErrorType.INSUFFICIENT_BALANCE
            }
        }

        // Check if user has access to the selected model
        if (!canUserAccessModel(userData, selectedModel)) {
            return {
                isAuthorized: false,
                errorType: ErrorType.MODEL_ACCESS_RESTRICTED
            }
        }

        // Estimate cost and check balance
        const textContent = (() => {
            let total = ''
            for (const message of generationData.inputObject.messages) {
                if (typeof message.content === 'string') total += message.content + '\n'
                else if (Array.isArray(message.content)) {
                    for (const part of message.content) {
                        if (part.type === 'text' && 'text' in part) total += part.text + '\n'
                    }
                }
            }
            return total
        })()
        const result = PricingCore.calculate({ model: selectedModel, textContent, applyProfitMultiplier: true })
        estimatedCost = result.totalCost
        const hasSufficientBalance = PricingCore.checkSufficientBalance(estimatedCost, userData.balance)
        if (!hasSufficientBalance) {
            return { 
                isAuthorized: false, 
                errorType: ErrorType.INSUFFICIENT_BALANCE,
                estimatedCost
            }
        }
        
        // Calculate max tokens based on user's remaining balance
        let maxTokensFromBalance = Infinity
        if (selectedModel.pricing.output_token) {
            maxTokensFromBalance = PricingCore.calculateMaxOutputTokens(userData.balance, estimatedCost, selectedModel)
        }

        // Calculate max tokens based on model's context window
        const modelContextWindow = selectedModel.architecture.contextLength || 200000
        let totalText = ''
        for (const message of generationData.inputObject.messages) {
            if (typeof message.content === 'string') {
                totalText += message.content + '\n'
            } else if (Array.isArray(message.content)) {
                for (const content of message.content) {
                    if (content.type === 'text') {
                        totalText += content.text + '\n'
                    }
                }
            }
        }
        const inputTokenCount = PricingCore.estimateTokenUsage(totalText, selectedModel.id)
        const maxTokensFromModel = modelContextWindow - inputTokenCount

        // Determine final max output tokens
        maxOutputTokens = Math.min(maxTokensFromBalance, maxTokensFromModel)
        maxOutputTokens = maxOutputTokens < 10000 ? maxOutputTokens : 10000
        
        return { 
            isAuthorized: true, 
            errorType: null,
            maxOutputTokens: Math.max(0, Math.floor(maxOutputTokens)), 
            estimatedCost 
        }
    }

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// For guest users
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    else {
        const { isValid, errorType } = await validateGuestUserContent(generationData)
        if (!isValid) {
            return { isAuthorized: false, errorType }
        }
        return { isAuthorized: true, errorType: null, maxOutputTokens: GUEST_USER_MAX_TOKENS }
    }
}




// Guest user content validation limits
const MAX_TOTAL_TOKENS_PER_CONVERSATION = 10000
const MAX_TOKENS_PER_MESSAGE = 1000
const ALLOWED_CONTENT_TYPES = ['text']

async function validateGuestUserContent(generationData: FormattedRequestObject): Promise<{ isValid: true, errorType: null } | { isValid: false, errorType: ErrorType }> {
    // Calculate total token count and validate content type in a single loop
    let totalTokenCount = 0
    let lastMessageTokenCount = 0
    const messages = generationData.inputObject.messages
    const lastMessageIndex = messages.length - 1
    
    for (let i = 0; i < messages.length; i++) {
        const message = messages[i]
        const isLastMessage = i === lastMessageIndex
        
        if (typeof message.content === 'string') {
            const tokenCount = PricingCore.estimateTokenUsage(message.content)
            totalTokenCount += tokenCount
            if (isLastMessage) {
                lastMessageTokenCount = tokenCount
            }
        } else if (Array.isArray(message.content)) {
            for (const part of message.content) {
                if (!ALLOWED_CONTENT_TYPES.includes(part.type)) {
                    return { 
                        isValid: false, 
                        errorType: ErrorType.USER_UNAUTHORIZED
                    }
                }
                
                // Check if part is a TextPart before accessing text property
                if (part.type === 'text' && 'text' in part) {
                    const tokenCount = PricingCore.estimateTokenUsage(part.text)
                    totalTokenCount += tokenCount
                    if (isLastMessage) {
                        lastMessageTokenCount += tokenCount
                    }
                }
            }
        }
    }
    
    // Check token count limits
    if (totalTokenCount > MAX_TOTAL_TOKENS_PER_CONVERSATION) {
        return { 
            isValid: false, 
            errorType: ErrorType.CONVERSATION_TOO_LONG 
        }
    }
    
    if (lastMessageTokenCount > MAX_TOKENS_PER_MESSAGE) {
        return { 
            isValid: false, 
            errorType: ErrorType.MESSAGE_TOO_LONG
        }
    }
    
    return { isValid: true, errorType: null }
}
